import type { Vec2 } from '$lib/core/geom';

export interface PlaceView {
  readonly center: Vec2;
  readonly zoom: number;
}

/** Where the person is: the open drawing, and the view of it when known. */
export interface Place {
  readonly drawingId: string;
  readonly view?: PlaceView;
}

export interface PlaceStore {
  read(): Place | null;
  /** Null takes the place out, as when the open drawing is not saved yet. */
  write(place: Place | null): void;
}

const KEYS = ['drawing', 'x', 'y', 'zoom'];

export function parsePlace(search: string): Place | null {
  const query = new URLSearchParams(search);
  const drawingId = query.get('drawing');
  if (!drawingId) return null;
  const [x, y, zoom] = ['x', 'y', 'zoom'].map((key) => Number(query.get(key) ?? NaN));
  if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(zoom) || !(zoom! > 0)) {
    return { drawingId };
  }
  return { drawingId, view: { center: { x: x!, y: y! }, zoom: zoom! } };
}

/** `search` with the place written over any place already in it, other params kept. */
export function withPlace(search: string, place: Place | null): string {
  const query = new URLSearchParams(search);
  for (const key of KEYS) query.delete(key);
  if (!place) return query.size > 0 ? `?${query.toString()}` : '';
  query.set('drawing', place.drawingId);
  if (place.view) {
    // To the nearest tenth of a pixel, so the URL stays short at any zoom.
    const digits = Math.max(0, Math.ceil(Math.log10(place.view.zoom)) + 1);
    query.set('x', String(Number(place.view.center.x.toFixed(digits))));
    query.set('y', String(Number(place.view.center.y.toFixed(digits))));
    query.set('zoom', String(Number(place.view.zoom.toPrecision(4))));
  }
  return `?${query.toString()}`;
}

/** The place in the page's URL, replaced in place so moving around adds no history. */
export class UrlPlaceStore implements PlaceStore {
  read(): Place | null {
    return typeof location === 'undefined' ? null : parsePlace(location.search);
  }

  write(place: Place | null): void {
    if (typeof location === 'undefined') return;
    const search = withPlace(location.search, place);
    if (search === location.search) return;
    history.replaceState(history.state, '', location.pathname + search + location.hash);
  }
}

export class MemoryPlaceStore implements PlaceStore {
  constructor(public place: Place | null = null) {}

  read(): Place | null {
    return this.place;
  }

  write(place: Place | null): void {
    this.place = place;
  }
}
